import api from "../../api/client";
import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import {
  Row,
  Col,
  Button,
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  Table,
  Card,
  Space,
  message,
  Tag,
  Badge,
  Spin,
  Descriptions,
  Statistic,
  Progress,
  Tooltip,
  Tabs,
  Alert,
  Divider,
  Typography,
} from "antd";
import {
  PlusOutlined,
  DownloadOutlined,
  SearchOutlined,
  ReloadOutlined,
  RollbackOutlined,
  ShoppingOutlined,
  CarOutlined,
  BarChartOutlined,
  RiseOutlined,
  // EggOutlined,
  WarningOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  MedicineBoxOutlined,
  PoundOutlined,
} from "@ant-design/icons";
import MainCard from "../../components/Card/MainCard";
import config from "../../config";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { FaEgg } from "react-icons/fa";

const { Option } = Select;
const { TextArea } = Input;
const { RangePicker } = DatePicker;
const { TabPane } = Tabs;
const { Text } = Typography;

const InternalConsumption = () => {
  const [data, setData] = useState([]);
  const [farms, setFarms] = useState([]);
  const [assetTypes, setAssetTypes] = useState([]);
  const [identifierOptions, setIdentifierOptions] = useState([]);
  const [poultryBatches, setPoultryBatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showReversalModal, setShowReversalModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [stats, setStats] = useState({
    summary: {},
    byType: [],
    topItems: [],
    monthly: [],
  });
  const [filters, setFilters] = useState({
    search: "",
    farm: "",
    item: "",
    reason: "",
    dates: [],
    status: "",
  });
  const [form] = Form.useForm();
  const [reversalForm] = Form.useForm();

  const account = useSelector((state) => state.account.user);

  const [activeTab, setActiveTab] = useState("stats");
  const [availableWaste, setAvailableWaste] = useState([]);

  // Fetch available waste items for the selected farm
  const fetchAvailableWaste = async (farm, itemType = null, search = "") => {
    if (!farm) return;
    try {
      const params = { farm, search };
      if (itemType && itemType.startsWith("waste_")) {
        params.waste_type = itemType; // e.g. "waste_hooves"
      }
      const res = await api.get(`/slaughter-waste/available`, { params });
      setAvailableWaste(res.data || []);
    } catch (err) {
      const msg =
        err?.response?.data?.error || err?.message || "Failed to load waste";
      console.error("Error fetching waste:", msg);
    }
  };

  // Egg item types for selection
  const eggItemTypes = [
    { value: "eggs", label: "Normal Eggs", category: "normal", unit: "trays" },
    {
      value: "mediumeggs",
      label: "Medium Eggs",
      category: "medium",
      unit: "trays",
    },
    {
      value: "doubleyolkeggs",
      label: "Double Yolk Eggs",
      category: "double_yolk",
      unit: "trays",
    },
    {
      value: "brokeneggs",
      label: "Broken Eggs",
      category: "broken",
      unit: "trays",
    },
  ];

  const wasteItemTypes = [
    {
      value: "waste_hooves",
      label: "Hooves",
      category: "waste_hooves",
      unit: "kg",
    },
    { value: "waste_head", label: "Head", category: "waste_head", unit: "kg" },
    { value: "waste_skin", label: "Skin", category: "waste_skin", unit: "kg" },
    {
      value: "waste_organs",
      label: "Internal Organs",
      category: "waste_organs",
      unit: "kg",
    },
  ];

  // Fetch all data on load and filter change
  useEffect(() => {
    fetchFarms();
    fetchAssetTypes();
    fetchPoultryBatches();
    fetchData();
    fetchStats();
  }, [filters]);

  // Fetch asset types from animal_assets table (excluding eggs)
  const fetchAssetTypes = async () => {
    try {
      const res = await api.get(`/asset-types-config`);
      // Filter out eggs from asset types since we handle them separately
      const filteredAssetTypes = (res.data || []).filter(
        (t) => t.category === "livestock",
      );
      const livestockTypes = (filteredAssetTypes || []).filter(
        (t) => t.slug !== "egg",
      );
      setAssetTypes(livestockTypes);
    } catch (error) {
      console.error("Error fetching asset types:", error);
    }
  };

  // Fetch poultry batches for egg consumption
  const fetchPoultryBatches = async () => {
    try {
      const res = await api.get(`/animal-assets/available`, {
        params: { type_slug: "poultry", status: "alive" },
      });
      setPoultryBatches(res.data || []);
    } catch (error) {
      console.error("Error fetching poultry batches:", error);
    }
  };

  // Fetch consumption data
  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.search) params.search = filters.search;
      if (filters.farm) params.farm = filters.farm;
      if (filters.item) params.item = filters.item;
      if (filters.reason) params.reason = filters.reason;
      if (filters.status) params.status = filters.status;
      if (filters.dates && filters.dates.length === 2) {
        params.startDate = filters.dates[0].format("YYYY-MM-DD");
        params.endDate = filters.dates[1].format("YYYY-MM-DD");
      }
      const res = await api.get(`/internal-consumptions`, {
        params,
      });
      setData(res.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      message.error("Failed to load consumption data");
    } finally {
      setLoading(false);
    }
  };

  // Fetch statistics
  const fetchStats = async () => {
    try {
      const params = {};
      if (filters.farm && filters.farm !== "all") params.farm = filters.farm;
      if (filters.dates && filters.dates.length === 2) {
        params.startDate = filters.dates[0].format("YYYY-MM-DD");
        params.endDate = filters.dates[1].format("YYYY-MM-DD");
      }
      const res = await api.get(`/consumptions/stats-v2`, {
        params,
      });
      setStats(res.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  // Fetch farms
  const fetchFarms = async () => {
    try {
      const res = await api.get(`/farm`);
      setFarms(res.data);
    } catch (error) {
      console.error("Error fetching farms:", error);
    }
  };

  // Fetch available items from animal_assets
  const fetchAvailableItems = async (farm, typeSlug) => {
    if (!farm || !typeSlug) return;

    try {
      const res = await api.get(`/animal-assets/available`, {
        params: { farm, type_slug: typeSlug, status: "alive,active" },
      });

      const options = res.data.map((item) => ({
        identifier_value: item.tagNumber || item.batchId,
        source_id: item.id,
        label: `${item.tagNumber || item.batchId} (Available: ${
          item.quantity
        }) - ${item.breed || ""}`,
        value: item.id,
        available_quantity: item.quantity,
        weight: item.weight,
      }));

      setIdentifierOptions(options);
    } catch (error) {
      console.error("Error fetching available items:", error);
    }
  };

  // Handle item type selection
  const handleItemTypeChange = (value) => {
    // Check if it's an egg type
    const isEgg = eggItemTypes.some((e) => e.value === value);
    const isWaste = wasteItemTypes.some((e) => e.value === value);

    if (isEgg) {
      const selectedEgg = eggItemTypes.find((e) => e.value === value);
      form.setFieldsValue({
        item_type: value,
        item_name: selectedEgg.label,
        unit: selectedEgg.unit,
        identifier_type: "batch",
        is_egg: true,
        egg_category: selectedEgg.category,
        identifier_value: null,
        quantity: null,
      });
    } else if (isWaste) {
      const selectedWaste = wasteItemTypes.find((e) => e.value === value);
      form.setFieldsValue({
        item_type: value,
        item_name: selectedWaste.label,
        unit: "kg",
        identifier_type: "slaughter",
        is_egg: false,
        is_waste: true,
        identifier_value: null,
        source_id: null,
        quantity: null,
        available_quantity: null,
      });
      const farm = form.getFieldValue("farm");
      if (farm) {
        // Pass the item_type so the backend filters by waste part
        fetchAvailableWaste(farm, value);
      }
    } else {
      const selected = assetTypes.find((t) => t.slug === value);
      if (selected) {
        const identifierType = selected.is_batch ? "batch" : "tag_number";
        form.setFieldsValue({
          item_type: selected.slug,
          item_name: selected.name,
          unit: selected.is_batch ? "birds" : "head",
          identifier_type: identifierType,
          is_egg: false,
          identifier_value: null,
          quantity: identifierType === "tag_number" ? 1 : null,
        });

        const farm = form.getFieldValue("farm");
        if (farm) {
          fetchAvailableItems(farm, selected.slug);
        }
      }
    }
  };

  // Handle identifier selection
  const handleIdentifierChange = (value) => {
    const selected = identifierOptions.find((opt) => opt.value === value);
    if (selected) {
      form.setFieldsValue({
        identifier_value: selected.identifier_value,
        source_id: selected.source_id,
        available_quantity: selected.available_quantity,
      });

      const itemType = form.getFieldValue("item_type");
      if (itemType !== "poultry") {
        form.setFieldsValue({ quantity: 1 });
      }
    }
  };

  // Handle farm change
  const handleFarmChange = (value) => {
    form.setFieldsValue({
      farm: value,
      identifier_value: null,
      source_id: null,
      available_quantity: null,
    });
    const itemType = form.getFieldValue("item_type");
    const isEgg = eggItemTypes.some((e) => e.value === itemType);
    const isWaste = wasteItemTypes.some((e) => e.value === itemType);

    if (itemType && !isEgg) {
      if (isWaste) {
        fetchAvailableWaste(value, itemType);
      } else {
        fetchAvailableItems(value, itemType);
      }
    }
  };

  const handleWasteSelection = (slaughterId) => {
    const selected = availableWaste.find(
      (w) =>
        w.slaughterId === slaughterId &&
        w.availableWeight > 0 &&
        w.wasteKey === form.getFieldValue("item_type"),
    );
    console.log("first", selected, slaughterId);
    if (selected) {
      form.setFieldsValue({
        identifier_value: selected.tagNumber,
        source_id: selected.slaughterId,
        available_quantity: selected.availableWeight,
      });
    }
  };

  // Submit consumption
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      if (!values.quantity || values.quantity <= 0) {
        message.error("Please enter a valid quantity");
        return;
      }

      // For tag-based items (non-batch, non-egg), ensure identifier is selected
      if (
        values.identifier_type === "tag_number" &&
        !values.identifier_value &&
        !values.is_egg
      ) {
        message.error("Please select an animal");
        return;
      }

      setSubmitting(true);

      const payload = {
        item_type: values.item_type,
        item_name: values.item_name,
        quantity: values.quantity,
        unit: values.unit,
        consumption_date: values.consumption_date.format("YYYY-MM-DD"),
        reason: values.reason,
        recorded_by: account.id,
        farm: values.farm,
        identifier_type: values.identifier_type,
        identifier_value: values.identifier_value,
        source_id: values.source_id,
        remarks: values.remarks,
        egg_type: values.egg_category || values.egg_type,
        is_egg: values.is_egg || false,
        is_waste: values.is_waste || false,
      };

      await api.post(`/internal-consumptions-v1`, payload);
      message.success("Consumption recorded successfully");
      setShowModal(false);
      form.resetFields();
      fetchData();
      fetchStats();
      fetchPoultryBatches();
    } catch (error) {
      console.error("Error submitting consumption:", error);
      message.error(
        error.response?.data?.error || "Failed to record consumption",
      );
    } finally {
      setSubmitting(false);
    }
  };

  // Open reversal modal
  const openReversalModal = (record) => {
    setSelectedRecord(record);
    reversalForm.setFieldsValue({
      reason: "wrong_entry",
    });
    setShowReversalModal(true);
  };

  // Reverse consumption
  const handleReverse = async () => {
    if (!selectedRecord) return;

    try {
      const values = await reversalForm.validateFields();
      setSubmitting(true);

      await api.post(`/internal-consumptions/${selectedRecord.id}/reverse`, {
        reason: values.reason,
        reason_detail: values.reason_detail,
        reversed_by: account.id,
        reversed_by_name: account.firstname,
      });

      message.success("Consumption reversed successfully");
      setShowReversalModal(false);
      setSelectedRecord(null);
      reversalForm.resetFields();
      fetchData();
      fetchStats();
      fetchPoultryBatches();
    } catch (error) {
      console.error("Error reversing consumption:", error);
      message.error(
        error.response?.data?.error || "Failed to reverse consumption",
      );
    } finally {
      setSubmitting(false);
    }
  };

  // Export to Excel
  const downloadExcel = () => {
    const exportData = data.map((row) => ({
      Date: dayjs(row.consumption_date).format("DD MMM YYYY"),
      Item: row.item_name,
      Type: row.item_type,
      EggType: row.egg_type || "-",
      Farm: farms.find((f) => f.id === Number(row.farm))?.name || row.farm,
      Identifier: row.identifier_value,
      Quantity: `${row.quantity} ${row.unit}`,
      Reason: row.reason,
      RecordedBy: row.firstname,
      Remarks: row.remarks,
      Status: row.status,
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Consumptions");
    const buffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const file = new Blob([buffer], { type: "application/octet-stream" });
    saveAs(file, `internal-consumption-${dayjs().format("YYYY-MM-DD")}.xlsx`);
  };

  // Get icon for item type
  const getItemIcon = (record) => {
    if (
      record.item_type === "eggs" ||
      record.item_type === "mediumeggs" ||
      record.item_type === "doubleyolkeggs" ||
      record.item_type === "brokeneggs"
    ) {
      return <FaEgg />;
    }
    return <ShoppingOutlined />;
  };

  // Table columns
  const columns = [
    {
      title: "Date",
      dataIndex: "consumption_date",
      width: 100,
      render: (d) => dayjs(d).format("DD MMM YYYY"),
    },
    {
      title: "Item",
      dataIndex: "item_name",
      width: 120,
      render: (t, r) => (
        <Space>
          {getItemIcon(r)}
          <span>{t}</span>
        </Space>
      ),
    },
    // { title: "Egg Type", dataIndex: "egg_type", width: 100, render: (t) => t ? <Tag color="gold">{t}</Tag> : "-" },
    {
      title: "Farm",
      width: 100,
      render: (_, r) =>
        farms.find((f) => f.id === Number(r.farm))?.name || r.farm,
    },
    {
      title: "Identifier",
      dataIndex: "identifier_value",
      width: 100,
      render: (t) => t || "—",
    },
    {
      title: "Quantity",
      width: 80,
      render: (_, r) => (
        <Text>
          {r.quantity} {r.unit}
        </Text>
      ),
    },
    { title: "Reason", dataIndex: "reason", width: 100 },
    {
      title: "Status",
      dataIndex: "status",
      width: 80,
      render: (s) => (
        <Tag
          color={
            s === "completed" ? "green" : s === "reversed" ? "red" : "orange"
          }
        >
          {s}
        </Tag>
      ),
    },
    // { title: "By", dataIndex: "recorded_by", width: 100 },
    {
      title: "Actions",
      width: 80,
      render: (_, r) =>
        r.status !== "reversed" && (
          <Tooltip title="Reverse">
            <Button
              icon={<RollbackOutlined />}
              size="small"
              danger
              onClick={() => openReversalModal(r)}
            />
          </Tooltip>
        ),
    },
  ];

  // Reset filters
  const resetFilters = () => {
    setFilters({
      search: "",
      farm: "",
      item: "",
      reason: "",
      dates: [],
      status: "",
    });
  };

  // Stats summary
  const summary = stats.summary || {};
  const byType = stats.byType || [];
  const topItems = stats.topItems || [];

  return (
    <>
      <Card
        // size="small"
        title="Internal Consumption"
        extra={
          <Row justify="space-between" align="middle" gutter={[16, 16]}>
            <Col xs={24} lg={18}>
              <Row gutter={[12, 12]}>
                <Col xs={24} sm={12} md={6}>
                  <Input
                    placeholder="Search"
                    allowClear
                    prefix={<SearchOutlined />}
                    onChange={(e) =>
                      setFilters({ ...filters, search: e.target.value })
                    }
                  />
                </Col>
                <Col xs={24} sm={12} md={4}>
                  <Select
                    placeholder="Farm"
                    allowClear
                    style={{ width: "100%" }}
                    onChange={(v) => setFilters({ ...filters, farm: v })}
                  >
                    {farms.map((f) => (
                      <Option key={f.id} value={f.id}>
                        {f.name}
                      </Option>
                    ))}
                  </Select>
                </Col>
                <Col xs={24} sm={12} md={4}>
                  <Select
                    placeholder="Item Type"
                    allowClear
                    style={{ width: "100%" }}
                    onChange={(v) => setFilters({ ...filters, item: v })}
                  >
                    <Option value="eggs">Eggs</Option>
                    <Option value="mediumeggs">Medium Eggs</Option>
                    <Option value="doubleyolkeggs">Double Yolk</Option>
                    <Option value="brokeneggs">Broken Eggs</Option>
                    {assetTypes.map((t) => (
                      <Option key={t.slug} value={t.slug}>
                        {t.name}
                      </Option>
                    ))}
                    {wasteItemTypes.map((w) => (
                      <Option key={w.value} value={w.value}>
                        <Space>
                          <span>{w.label}</span>
                          <Tag color="orange">kg</Tag>
                        </Space>
                      </Option>
                    ))}
                  </Select>
                </Col>
                <Col xs={24} sm={12} md={4}>
                  <Select
                    placeholder="Status"
                    allowClear
                    style={{ width: "100%" }}
                    onChange={(v) => setFilters({ ...filters, status: v })}
                  >
                    <Option value="completed">Completed</Option>
                    <Option value="reversed">Reversed</Option>
                  </Select>
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <RangePicker
                    style={{ width: "100%" }}
                    onChange={(d) => setFilters({ ...filters, dates: d || [] })}
                  />
                </Col>
              </Row>
            </Col>
            <Col xs={24} lg={6}>
              <Space style={{ width: "100%", justifyContent: "flex-end" }}>
                <Button
                  size="small"
                  icon={<ReloadOutlined />}
                  onClick={resetFilters}
                >
                  Reset
                </Button>
                <Button
                  size="small"
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => setShowModal(true)}
                >
                  Record
                </Button>
                <Button
                  size="small"
                  icon={<DownloadOutlined />}
                  onClick={downloadExcel}
                >
                  Export
                </Button>
              </Space>
            </Col>
          </Row>
        }
      >
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <TabPane
            tab={
              <span>
                <ShoppingOutlined /> Stats
              </span>
            }
            key="stats"
          >
            {/* Statistics Cards */}
            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
              <Col xs={24} sm={12} lg={6}>
                <Card size="small">
                  <Statistic
                    title="Total Consumptions"
                    value={summary.total_consumptions || 0}
                    prefix={<ShoppingOutlined />}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Card size="small">
                  <Statistic
                    title="Total Quantity"
                    value={summary.total_quantity || 0}
                    suffix="units"
                    prefix={<BarChartOutlined />}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Card size="small">
                  <Statistic
                    title="Completed"
                    value={summary.completed_count || 0}
                    prefix={<CheckCircleOutlined />}
                    valueStyle={{ color: "#52c41a" }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Card size="small">
                  <Statistic
                    title="Reversed"
                    value={summary.reversed_count || 0}
                    prefix={<CloseCircleOutlined />}
                    valueStyle={{ color: "#ff4d4f" }}
                  />
                </Card>
              </Col>
            </Row>

            {/* Consumption by Type */}
            {byType.length > 0 && (
              <Card size="small" style={{ marginBottom: 16 }}>
                <div style={{ marginBottom: 8 }}>
                  <strong>Consumption by Type</strong>
                </div>
                <Row gutter={[16, 8]}>
                  {byType.map((type) => (
                    <Col span={8} key={type.item_type}>
                      <div
                        style={{
                          fontSize: 12,
                          color: "#666",
                          textTransform: "capitalize",
                        }}
                      >
                        {type.item_type}
                      </div>
                      <Progress
                        percent={Math.min(
                          (type.total_quantity / summary.total_quantity) * 100,
                          100,
                        ).toFixed(2)}
                        size="small"
                      />
                      <div>
                        {type.total_quantity} units ({type.count} records)
                      </div>
                    </Col>
                  ))}
                </Row>
              </Card>
            )}

            {/* Top Consumed Items */}
            {topItems.length > 0 && (
              <Card size="small" style={{ marginBottom: 16 }}>
                <div style={{ marginBottom: 8 }}>
                  <strong>Top Consumed Items</strong>
                </div>
                <Row gutter={[16, 8]}>
                  {topItems.slice(0, 6).map((item) => (
                    <Col span={8} key={item.item_name}>
                      <Card size="small" style={{ background: "#fafafa" }}>
                        <div>
                          <strong>{item.item_name}</strong>
                        </div>
                        <div style={{ fontSize: 12 }}>
                          Quantity: {item.total_quantity} units
                        </div>
                        <div style={{ color: "#666" }}>
                          {item.times_consumed} times
                        </div>
                      </Card>
                    </Col>
                  ))}
                </Row>
              </Card>
            )}
          </TabPane>

          <TabPane
            tab={
              <span>
                <MedicineBoxOutlined /> Consumption
              </span>
            }
            key="consumptions"
          >
            {/* Data Table */}
            <Table
              size="small"
              columns={columns}
              dataSource={data}
              loading={loading}
              rowKey="id"
              scroll={{ x: 1200 }}
              pagination={{
                showSizeChanger: true,
                showQuickJumper: true,
              }}
            />
          </TabPane>
        </Tabs>
        {/* Consumption Modal */}
        <Modal
          title="Record Internal Consumption"
          open={showModal}
          onCancel={() => {
            setShowModal(false);
            form.resetFields();
          }}
          onOk={handleSubmit}
          confirmLoading={submitting}
          width={650}
        >
          <Form
            form={form}
            layout="vertical"
            initialValues={{ consumption_date: dayjs() }}
          >
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="farm"
                  label="Farm"
                  rules={[{ required: true }]}
                >
                  <Select
                    placeholder="Select farm"
                    onChange={handleFarmChange}
                    showSearch
                  >
                    {farms.map((f) => (
                      <Option key={f.id} value={f.id}>
                        {f.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="item_type"
                  label="Item Type"
                  rules={[{ required: true }]}
                >
                  <Select
                    placeholder="Select item type"
                    onChange={handleItemTypeChange}
                    showSearch
                  >
                    <Select.OptGroup label="EGGS">
                      {eggItemTypes.map((egg) => (
                        <Option key={egg.value} value={egg.value}>
                          <Space>
                            <span>{egg.label}</span>
                          </Space>
                        </Option>
                      ))}
                    </Select.OptGroup>
                    <Select.OptGroup label="🐄 SLAUGHTER WASTE">
                      {wasteItemTypes.map((w) => (
                        <Option key={w.value} value={w.value}>
                          <Space>
                            <span>{w.label}</span>
                            <Tag color="orange">kg</Tag>
                          </Space>
                        </Option>
                      ))}
                    </Select.OptGroup>
                    <Select.OptGroup label="🐔 LIVESTOCK">
                      {assetTypes.map((t) => (
                        <Option key={t.slug} value={t.slug}>
                          <Space>
                            <span>{t.name}</span>
                            {t.is_batch && <Tag color="blue">Batch</Tag>}
                          </Space>
                        </Option>
                      ))}
                    </Select.OptGroup>
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            {/* Poultry Batch Selection (for eggs) */}
            <Form.Item noStyle shouldUpdate>
              {({ getFieldValue }) => {
                const itemType = getFieldValue("item_type");
                const isEgg = eggItemTypes.some((e) => e.value === itemType);

                return (
                  isEgg && (
                    <Form.Item
                      name="identifier_value"
                      label="Poultry Batch"
                      rules={[{ required: true }]}
                    >
                      <Select
                        placeholder="Select poultry batch"
                        onChange={handleIdentifierChange}
                        showSearch
                      >
                        {poultryBatches.map((batch) => (
                          <Option key={batch.id} value={batch.id}>
                            {batch.batchId} - {batch.quantity} birds available
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  )
                );
              }}
            </Form.Item>

            {/* Waste selection (from slaughter_waste) */}
            <Form.Item noStyle shouldUpdate>
              {({ getFieldValue }) => {
                const itemType = getFieldValue("item_type");
                const isWaste = wasteItemTypes.some(
                  (e) => e.value === itemType,
                );

                if (!isWaste) return null;

                // item_type "waste_hooves" → part key "hooves"
                const expectedKey =
                  itemType === "waste_organs"
                    ? "organs"
                    : itemType.replace("waste_", "");
                const matching = availableWaste.filter(
                  (w) => w.wasteKey === expectedKey,
                );

                return (
                  <Form.Item
                    name="identifier_value"
                    label={`Source animal (${itemType.replace("waste_", "")})`}
                    rules={[
                      {
                        required: true,
                        message: "Select the slaughter record",
                      },
                    ]}
                  >
                    <Select
                      placeholder={
                        matching.length
                          ? "Select tag number"
                          : "No slaughter records with this waste part available"
                      }
                      onChange={(slaughterId) => {
                        const sel = matching.find(
                          (w) => w.slaughterId === slaughterId,
                        );
                        if (sel) {
                          form.setFieldsValue({
                            identifier_value: sel.tagNumber,
                            source_id: sel.slaughterId,
                            available_quantity: sel.availableWeight,
                            waste_key: sel.wasteKey,
                          });
                        }
                      }}
                      showSearch
                      optionFilterProp="children"
                      notFoundContent="No records with remaining quantity"
                    >
                      {matching.map((w) => (
                        <Option key={w.slaughterId} value={w.slaughterId}>
                          <Space>
                            <span>
                              {w.tagNumber} - {w.animalName || "Unnamed"}
                            </span>
                            <Tag color="orange">
                              {w.availableWeight} kg available
                            </Tag>
                          </Space>
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                );
              }}
            </Form.Item>

            {/* Animal Selection (for livestock) */}
            <Form.Item noStyle shouldUpdate>
              {({ getFieldValue }) => {
                const itemType = getFieldValue("item_type");
                const isEgg = eggItemTypes.some((e) => e.value === itemType);
                const isPoultry = itemType === "poultry";

                return (
                  itemType &&
                  !isEgg && (
                    <Form.Item
                      name="identifier_value"
                      label={isPoultry ? "Batch Number" : "Tag Number"}
                    >
                      <Select
                        placeholder={
                          isPoultry ? "Select batch" : "Select animal"
                        }
                        onChange={handleIdentifierChange}
                        showSearch
                        loading={
                          !identifierOptions.length && getFieldValue("farm")
                        }
                        notFoundContent="No items available"
                      >
                        {identifierOptions.map((opt) => (
                          <Option key={opt.value} value={opt.value}>
                            <Space>
                              <span>{opt.label}</span>
                              <Tag color="green">
                                {opt.available_quantity} available
                              </Tag>
                            </Space>
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  )
                );
              }}
            </Form.Item>

            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  name="quantity"
                  label="Quantity"
                  rules={[{ required: true }]}
                >
                  <Input
                    type="number"
                    min={0.01}
                    step={0.01}
                    placeholder="Enter quantity"
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="unit" label="Unit">
                  <Input disabled />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="consumption_date"
                  label="Date"
                  rules={[{ required: true }]}
                >
                  <DatePicker style={{ width: "100%" }} format="YYYY-MM-DD" />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item name="reason" label="Beneficiary/Reason">
              <Select placeholder="Select beneficiary" allowClear>
                <Option value="Florence">Florence</Option>
                <Option value="Pastor">Pastor</Option>
                <Option value="All Farms">All Farms</Option>
                <Option value="Others">Others</Option>
              </Select>
            </Form.Item>

            <Form.Item name="remarks" label="Remarks">
              <TextArea rows={2} placeholder="Additional notes..." />
            </Form.Item>

            {/* Hidden fields */}
            <Form.Item name="item_name" hidden>
              <Input />
            </Form.Item>
            <Form.Item name="identifier_type" hidden>
              <Input />
            </Form.Item>
            <Form.Item name="source_id" hidden>
              <Input />
            </Form.Item>
            <Form.Item name="is_egg" hidden>
              <Input />
            </Form.Item>
            <Form.Item name="egg_category" hidden>
              <Input />
            </Form.Item>
            <Form.Item name="is_waste" hidden>
              <Input />
            </Form.Item>
            <Form.Item name="waste_key" hidden>
              <Input />
            </Form.Item>
          </Form>
        </Modal>

        {/* Reversal Modal */}
        <Modal
          title={
            <Space>
              <RollbackOutlined /> Reverse Consumption
            </Space>
          }
          open={showReversalModal}
          onCancel={() => {
            setShowReversalModal(false);
            setSelectedRecord(null);
            reversalForm.resetFields();
          }}
          onOk={handleReverse}
          confirmLoading={submitting}
          okButtonProps={{ danger: true }}
          width={500}
        >
          <Alert
            message="Warning: This action cannot be undone"
            description="Reversing will restore the consumed inventory to its original state."
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
          />

          {selectedRecord && (
            <Descriptions
              size="small"
              bordered
              column={1}
              style={{ marginBottom: 16 }}
            >
              <Descriptions.Item label="Item">
                {selectedRecord.item_name}
              </Descriptions.Item>
              {selectedRecord.egg_type && (
                <Descriptions.Item label="Egg Type">
                  {selectedRecord.egg_type}
                </Descriptions.Item>
              )}
              <Descriptions.Item label="Quantity">
                {selectedRecord.quantity} {selectedRecord.unit}
              </Descriptions.Item>
              <Descriptions.Item label="Date">
                {dayjs(selectedRecord.consumption_date).format("DD MMM YYYY")}
              </Descriptions.Item>
              <Descriptions.Item label="Beneficiary">
                {selectedRecord.reason || "-"}
              </Descriptions.Item>
            </Descriptions>
          )}

          <Form form={reversalForm} layout="vertical">
            <Form.Item
              name="reason"
              label="Reversal Reason"
              rules={[{ required: true }]}
            >
              <Select placeholder="Select a reason">
                <Option value="wrong_entry">
                  Wrong entry / Data entry error
                </Option>
                <Option value="duplicate">Duplicate entry</Option>
                <Option value="cancelled">Cancelled by management</Option>
                <Option value="other">Other</Option>
              </Select>
            </Form.Item>

            <Form.Item name="reason_detail" label="Additional Details">
              <TextArea rows={2} placeholder="Provide more details..." />
            </Form.Item>
          </Form>
        </Modal>
      </Card>
    </>
  );
};

export default InternalConsumption;
